import { Button } from '@1trading/react';
import type { FilterConfig } from '@/components/filter-bar';
import { TableFilters } from '@/components/filter-bar';
import { TRANSFER_DIRECTIONS, TRANSFER_METHOD_TYPES, TRANSFER_PROVIDERS, TRANSFER_STATUSES, type TransactionFilters } from '@/services/api/funds-transactions';

/**
 * Column configs (legacy order/labels), now on the shared filter-bar module.
 * Filtering itself is SERVER-SIDE (bop-api applies the predicate via
 * buildTransactionsSearchRequest in transactions-panel.tsx) — `getValue` is never invoked
 * here: TableFilters only calls it through `deriveFilterOptions` for `multi-choice` columns
 * lacking a static `options` list, and every choice column below ships one, so that path is
 * unreachable. `matchMode: 'substring'` on the text columns is cosmetic only in this file
 * (it just picks the FilterEditDialog helper copy) — the legacy `text` column type these
 * ported from is substring-matched by the API itself, consistent with the shared module's
 * `text` → `substring` legacy mapping (see filter-bar/types.ts MatchMode doc).
 *
 * Each choice column's options are sorted, matching the legacy dialog's
 * `[...options].sort()` — TRANSFER_METHOD_TYPES in particular is NOT declared in sorted
 * order (`SEPA_INSTANT` precedes `SEPA`), so this is a real, preserved display-order behavior.
 *
 * `matchMode: 'substring'` on the text columns only picked the dialog's helper copy — bop-api's
 * actual matching behavior for these fields was never verified from this codebase (verification
 * flagged asserting "case-insensitive substring" as an unconfirmed, potentially
 * misleading claim), so `helperText: ''` suppresses that claim rather than guessing.
 */
const TEXT_HELPER_TEXT = '';

const TRANSACTION_FILTER_CONFIGS: Array<FilterConfig<unknown>> = [
  { getValue: () => undefined, helperText: TEXT_HELPER_TEXT, key: 'account_holder_id', matchMode: 'substring', title: 'Account Holder PID', type: 'multi-text' },
  { getValue: () => undefined, helperText: TEXT_HELPER_TEXT, key: 'account_id', matchMode: 'substring', title: 'Account ID', type: 'multi-text' },
  { getValue: () => undefined, key: 'direction', options: [...TRANSFER_DIRECTIONS].sort(), title: 'Direction', type: 'multi-choice' },
  { getValue: () => undefined, helperText: TEXT_HELPER_TEXT, key: 'currency', matchMode: 'substring', title: 'Currency', type: 'multi-text' },
  { getValue: () => undefined, key: 'transfer_status', options: [...TRANSFER_STATUSES].sort(), title: 'Status', type: 'multi-choice' },
  { getValue: () => undefined, key: 'transfer_provider', options: [...TRANSFER_PROVIDERS].sort(), title: 'Provider', type: 'multi-choice' },
  { getValue: () => undefined, key: 'transfer_method_type', options: [...TRANSFER_METHOD_TYPES].sort(), title: 'Method', type: 'multi-choice' },
  { getValue: () => undefined, helperText: TEXT_HELPER_TEXT, key: 'transaction_id', matchMode: 'substring', title: 'Transaction ID', type: 'multi-text' },
  { getValue: () => undefined, helperText: TEXT_HELPER_TEXT, key: 'broker_batch_id', matchMode: 'substring', title: 'Broker Batch ID', type: 'multi-text' },
];

/** Every config except the one whose chip is replaced by a locked, non-interactive badge. */
const UNLOCKED_CONFIGS = TRANSACTION_FILTER_CONFIGS.filter((config) => config.key !== 'account_holder_id');

export interface TransactionFiltersRowProps {
  filters: TransactionFilters;
  onChange: (filters: TransactionFilters) => void;
  /** User-page embed: the PID is forced by the prop, so its chip is shown locked, not filterable */
  isAccountHolderLocked?: boolean;
}

export const TransactionFiltersRow = ({ filters, onChange, isAccountHolderLocked = false }: TransactionFiltersRowProps) => (
  <div className="flex flex-wrap items-center gap-2">
    <TableFilters
      configs={isAccountHolderLocked ? UNLOCKED_CONFIGS : TRANSACTION_FILTER_CONFIGS}
      onChange={(next) => onChange(next as TransactionFilters)}
      rows={[]}
      state={filters}
    />
    {/* TableFilters has no per-chip disabled affordance, so the locked PID chip renders
        alongside it rather than in its original (first) position — cosmetic ordering only,
        the account_holder_id filter itself is never editable here either way. */}
    {isAccountHolderLocked ? (
      <Button className="rounded-full" disabled size="sm" type="button" variant="outline">
        Account Holder PID
      </Button>
    ) : null}
  </div>
);
TransactionFiltersRow.displayName = 'TransactionFiltersRow';
